// views/Dashboard/PainelExecutivo/sections/ceo/useContextoKpis.ts
//
// Junta o que cada área já carrega (faturas, funil do CRM, mídia paga, geração,
// paradas, chamados) no formato que o catálogo de KPIs automáticos lê. Os hooks
// são os MESMOS das abas de detalhe — o número do KPI é o número da aba.
import { useMemo } from 'react';
import { useFinanceiroDashboardData } from '../financeiro/useFinanceiroDashboardData';
import { useComercialDashboardData } from '../comercial/useComercialDashboardData';
import { useMarketingDashboardData } from '../marketing/useMarketingDashboardData';
import { useChamadosTi } from './useCeoDados';
import type { useMetasFaturamento } from './useCeoDados';
import { FILTROS_DASHBOARD_INICIAIS } from '../../types';
import type { PeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { calcularJanelaPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { janelaParaPeriodoComercial } from '../../../../../src/lib/dashboard/janelaParaPeriodoComercial';
import { filtrarPorDepartamento } from '../../../../../src/lib/dashboard/filtrarNegociacoesPorDepartamento';
import { computeEventFunnel } from '../../../../CRM/Analytics/eventFunnel';
import {
  financeiroPorUsina, fluxoEnergia, geracaoPorUsina, isoDoMes, matrizGeracao, mesDoOrdinal, metaVigente, ordinalDoMes, totalGeracao,
} from '../../../../../src/lib/dashboard/painelCeo';
import type { EventoParada, LinhaVsMeta, UsinaCeo } from '../../../../../src/lib/dashboard/painelCeo';
import type { ContextoKpis, NumerosComercial } from '../../../../../src/lib/dashboard/kpisAutomaticos';
import { parteVigorDaFatura } from '../../../../../src/lib/financeiro/parteVigor';

// Fora do componente: objeto novo a cada render refaria a busca de marketing sem parar.
const MES_ATUAL: PeriodoDashboard = { tipo: 'preset', preset: 'mesAtual' };
const MES_ANTERIOR: PeriodoDashboard = { tipo: 'preset', preset: 'mesAnterior' };

/**
 * Funil do CRM no mês de `agora` e no anterior, todos os canais: o que os KPIs
 * comerciais leem. Fora do gancho para a tela da Gestão Comercial usar a mesma conta
 * sem carregar usinas, mídia e chamados.
 */
export function numerosComerciaisDoMes(
  dados: Parameters<typeof filtrarPorDepartamento>[0],
  profilesById: Parameters<typeof filtrarPorDepartamento>[1],
  agora: Date,
): { atual: NumerosComercial; anterior: NumerosComercial } {
  const filtradas = filtrarPorDepartamento(dados, profilesById, FILTROS_DASHBOARD_INICIAIS.departamentos);
  const numeros = (p: PeriodoDashboard): NumerosComercial => {
    const { periodo, customRange } = janelaParaPeriodoComercial(calcularJanelaPeriodoDashboard(p, agora), agora);
    const etapas = computeEventFunnel(filtradas as Parameters<typeof computeEventFunnel>[0], periodo, customRange);
    const de = (k: string) => etapas.find((s) => s.key === k);
    return {
      prospeccoes: de('prospeccao')?.leads ?? 0,
      assinados: de('termo_assinado')?.leads ?? 0,
      aprovados: de('termo_aprovado')?.leads ?? 0,
      kwhAprovados: de('termo_aprovado')?.kwh ?? 0,
    };
  };
  return { atual: numeros(MES_ATUAL), anterior: numeros(MES_ANTERIOR) };
}

interface Entrada {
  agora: Date;
  usinas: UsinaCeo[];
  fator: number;
  linhas: LinhaVsMeta[];
  eventos: EventoParada[];
  paradasCarregando: boolean;
  mesFechado: string;
  metas: ReturnType<typeof useMetasFaturamento>;
}

export function useContextoKpis({ agora, usinas, fator, linhas, eventos, paradasCarregando, mesFechado, metas }: Entrada): ContextoKpis {
  const fin = useFinanceiroDashboardData();
  const com = useComercialDashboardData();
  const mktAtual = useMarketingDashboardData(MES_ATUAL);
  const mktAnterior = useMarketingDashboardData(MES_ANTERIOR);
  const chamados = useChamadosTi(agora);

  const parteVigor = useMemo(
    () => (f: Parameters<typeof parteVigorDaFatura>[0]) => parteVigorDaFatura(f, fin.percentualVigorPorParceira),
    [fin.percentualVigorPorParceira],
  );

  const comercial = useMemo(() => {
    if (com.loading && com.dados.length === 0) return null;
    return numerosComerciaisDoMes(com.dados, com.profilesById, agora);
  }, [com.dados, com.profilesById, com.loading, agora]);

  const marketing = useMemo(() => {
    if ((mktAtual.loading && mktAtual.byDay.length === 0) || (mktAnterior.loading && mktAnterior.byDay.length === 0)) return null;
    const n = (k: typeof mktAtual.kpis) => ({ investimento: k.totalSpend, leads: k.totalLeads, resultados: k.totalResults });
    return { atual: n(mktAtual.kpis), anterior: n(mktAnterior.kpis) };
  }, [mktAtual.kpis, mktAnterior.kpis, mktAtual.loading, mktAnterior.loading, mktAtual.byDay.length, mktAnterior.byDay.length]);

  const geracao = useMemo(() => {
    if (usinas.length === 0 || linhas.length === 0) return null;
    const ord = agora.getFullYear() * 12 + agora.getMonth();
    const iso = (o: number) => isoDoMes(mesDoOrdinal(o)) ?? '';
    const doMes = linhas.filter((l) => String(l.mes_ref ?? '').slice(0, 10) === iso(ord));
    const matriz = matrizGeracao(usinas, linhas, fator);
    return {
      atual: totalGeracao(geracaoPorUsina(usinas, doMes, fator)).atingimento,
      anterior: matriz.total[iso(ord - 1)]?.atingimento ?? null,
    };
  }, [usinas, linhas, fator, agora]);

  const paradas = useMemo(() => {
    if (paradasCarregando) return null;
    return {
      horas: eventos.reduce((t, e) => t + e.horasUsina, 0),
      horasComMotivo: eventos.filter((e) => e.causa === 'registrado').reduce((t, e) => t + e.horasUsina, 0),
    };
  }, [eventos, paradasCarregando]);

  const energia = useMemo(() => {
    if (fin.dados.length === 0 || linhas.length === 0) return null;
    const uso = (mes: string) => {
      const iso = isoDoMes(mes) ?? '';
      const f = fluxoEnergia(
        usinas,
        financeiroPorUsina(fin.dados, usinas, mes, parteVigor),
        geracaoPorUsina(usinas, linhas.filter((l) => String(l.mes_ref ?? '').slice(0, 10) === iso), fator),
      );
      const produzido = f.reduce((t, x) => t + (x.produzidoKwh ?? 0), 0);
      return produzido > 0 ? f.reduce((t, x) => t + x.usadoClientesKwh, 0) / produzido : null;
    };
    return { usoAtual: uso(mesFechado), usoAnterior: uso(mesDoOrdinal((ordinalDoMes(mesFechado) ?? 0) - 1)) };
  }, [fin.dados, linhas, usinas, mesFechado, parteVigor, fator]);

  const metaFaturamentoUsinas = useMemo(() => {
    const iso = isoDoMes(mesFechado) ?? '';
    return usinas.reduce((t, u) => t + (metaVigente(metas.metas, u.id, iso) ?? 0), 0);
  }, [usinas, metas.metas, mesFechado]);

  return useMemo(() => ({
    hoje: agora,
    faturas: fin.dados.length ? fin.dados : null,
    parteVigor: fin.dados.length ? parteVigor : null,
    metaFaturamentoUsinas,
    comercial,
    marketing,
    geracao,
    paradas,
    energia,
    chamados: chamados.numeros,
  }), [agora, fin.dados, parteVigor, metaFaturamentoUsinas, comercial, marketing, geracao, paradas, energia, chamados.numeros]);
}
